class UGCVideoCarousel {
  constructor() {
    this.carousel = document.querySelector('[data-ugc-carousel]');
    this.videoCards = document.querySelectorAll('[data-video-card]');
    this.previewVideos = document.querySelectorAll('[data-preview-video]');
    this.modal = document.querySelector('[data-video-modal]');
    this.modalVideo = document.querySelector('[data-modal-video]');
    this.closeButton = document.querySelector('[data-modal-close]');
    this.prevButton = document.querySelector('[data-modal-prev]');
    this.nextButton = document.querySelector('[data-modal-next]');
    this.muteButton = document.querySelector('[data-modal-mute]');
    this.muteIcon = document.querySelector('[data-mute-icon]');
    this.unmuteIcon = document.querySelector('[data-unmute-icon]');
    
    this.currentPreviewIndex = 0;
    this.currentModalIndex = 0;
    this.previewInterval = null;
    this.previewDuration = 3000; // 3 seconds
    this.videoData = window.ugcVideoData || [];
    this.isMuted = false;
    
    this.init();
  }
  
  init() {
    if (!this.carousel || this.videoCards.length === 0) return;
    
    // Load video durations
    this.loadVideoDurations();
    
    // Start preview rotation
    this.startPreviewRotation();
    
    // Add click handlers to video cards
    this.videoCards.forEach((card, index) => {
      card.addEventListener('click', () => this.openModal(index));
      card.setAttribute('tabindex', '0');
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openModal(index);
        }
      });
    });
    
    // Modal controls
    this.closeButton?.addEventListener('click', () => this.closeModal());
    this.prevButton?.addEventListener('click', () => this.navigateModal('prev'));
    this.nextButton?.addEventListener('click', () => this.navigateModal('next'));
    this.muteButton?.addEventListener('click', () => this.toggleMute());
    
    // Click video to pause/play
    this.modalVideo?.addEventListener('click', () => this.togglePlayPause());
    
    // Keyboard controls
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));
    
    // Prevent body scroll when modal is open
    this.modal?.addEventListener('transitionend', () => {
      if (this.modal.getAttribute('aria-hidden') === 'false') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }
  
  loadVideoDurations() {
    this.videoCards.forEach((card, index) => {
      const videoUrl = this.videoData[index]?.fullVideo;
      if (!videoUrl) return;
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      const updateDuration = () => {
        const duration = Math.floor(video.duration);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        const durationText = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        
        const durationElement = card.querySelector('[data-duration] .ugc-duration-text');
        if (durationElement) {
          durationElement.textContent = durationText;
        }
      };
      
      // Check if video is HLS (m3u8) and browser supports HLS.js
      if (videoUrl.includes('.m3u8') && typeof Hls !== 'undefined' && Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.addEventListener('loadedmetadata', () => {
            updateDuration();
            hls.destroy();
          });
        });
      } else {
        // Regular MP4 or native HLS support (Safari)
        video.addEventListener('loadedmetadata', updateDuration);
        video.src = videoUrl;
      }
    });
  }
  
  startPreviewRotation() {
    // Play first preview immediately
    this.playPreview(0);
    
    // Set up rotation interval
    this.previewInterval = setInterval(() => {
      this.currentPreviewIndex = (this.currentPreviewIndex + 1) % this.previewVideos.length;
      this.playPreview(this.currentPreviewIndex);
    }, this.previewDuration);
  }
  
  playPreview(index) {
    // Stop all previews and reset to first frame
    this.previewVideos.forEach((video, i) => {
      video.pause();
      video.currentTime = 0;
    });
    
    // Play current preview
    const currentVideo = this.previewVideos[index];
    if (currentVideo) {
      const playPromise = currentVideo.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.log('Preview autoplay prevented:', err);
          // If autoplay fails, try again with user interaction
        });
      }
      
      // Auto-scroll if needed
      this.scrollToVideoIfNeeded(index);
    }
  }
  
  scrollToVideoIfNeeded(index) {
    const card = this.videoCards[index];
    if (!card) return;
    
    const carousel = this.carousel;
    const cardRect = card.getBoundingClientRect();
    const carouselRect = carousel.getBoundingClientRect();
    
    // Check if card is fully visible
    const isVisible = (
      cardRect.left >= carouselRect.left &&
      cardRect.right <= carouselRect.right
    );
    
    if (!isVisible) {
      // Scroll to center the card
      const scrollLeft = card.offsetLeft - (carousel.offsetWidth / 2) + (card.offsetWidth / 2);
      carousel.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }
  
  openModal(index) {
    this.currentModalIndex = index;
    const videoUrl = this.videoData[index]?.fullVideo;
    
    console.log('Opening modal for video:', index, 'URL:', videoUrl); // Debug
    
    if (!videoUrl || !this.modal || !this.modalVideo) {
      console.error('Missing data:', { videoUrl, modal: !!this.modal, modalVideo: !!this.modalVideo });
      return;
    }
    
    // Destroy existing HLS instance if any
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    
    // Clear existing sources
    this.modalVideo.innerHTML = '';
    
    // Check if video is HLS (m3u8) and browser supports HLS.js
    if (videoUrl.includes('.m3u8') && typeof Hls !== 'undefined') {
      if (Hls.isSupported()) {
        // Use HLS.js for m3u8 playback
        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false
        });
        this.hls.loadSource(videoUrl);
        this.hls.attachMedia(this.modalVideo);
        
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest loaded, ready to play');
        });
        
        this.hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
        });
      } else if (this.modalVideo.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        this.modalVideo.src = videoUrl;
      }
    } else {
      // Regular MP4 video
      this.modalVideo.src = videoUrl;
    }
    
    // Load the video
    this.modalVideo.load();
    
    // Show modal
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Play video
    this.modalVideo.play().catch(err => {
      console.log('Video autoplay prevented:', err);
    });
    
    // Focus close button for accessibility
    setTimeout(() => this.closeButton?.focus(), 100);
  }
  
  closeModal() {
    if (!this.modal || !this.modalVideo) return;
    
    // Pause and reset video
    this.modalVideo.pause();
    this.modalVideo.src = '';
    
    // Destroy HLS instance if exists
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    
    // Hide modal
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Return focus to the card that was opened
    const card = this.videoCards[this.currentModalIndex];
    if (card) {
      card.focus();
    }
  }
  
  navigateModal(direction) {
    const totalVideos = this.videoData.length;
    
    if (direction === 'prev') {
      this.currentModalIndex = (this.currentModalIndex - 1 + totalVideos) % totalVideos;
    } else {
      this.currentModalIndex = (this.currentModalIndex + 1) % totalVideos;
    }
    
    const videoUrl = this.videoData[this.currentModalIndex]?.fullVideo;
    if (!videoUrl || !this.modalVideo) return;
    
    // Destroy existing HLS instance if any
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    
    // Clear existing sources
    this.modalVideo.innerHTML = '';
    
    // Check if video is HLS (m3u8) and browser supports HLS.js
    if (videoUrl.includes('.m3u8') && typeof Hls !== 'undefined') {
      if (Hls.isSupported()) {
        // Use HLS.js for m3u8 playback
        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false
        });
        this.hls.loadSource(videoUrl);
        this.hls.attachMedia(this.modalVideo);
      } else if (this.modalVideo.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        this.modalVideo.src = videoUrl;
      }
    } else {
      // Regular MP4 video
      this.modalVideo.src = videoUrl;
    }
    
    // Load and play
    this.modalVideo.load();
    this.modalVideo.play().catch(err => {
      console.log('Video autoplay prevented:', err);
    });
  }
  
  togglePlayPause() {
    if (!this.modalVideo) return;
    
    if (this.modalVideo.paused) {
      this.modalVideo.play();
    } else {
      this.modalVideo.pause();
    }
  }
  
  toggleMute() {
    if (!this.modalVideo) return;
    
    this.isMuted = !this.isMuted;
    this.modalVideo.muted = this.isMuted;
    
    // Update icon
    if (this.isMuted) {
      this.muteIcon.style.display = 'none';
      this.unmuteIcon.style.display = 'block';
    } else {
      this.muteIcon.style.display = 'block';
      this.unmuteIcon.style.display = 'none';
    }
  }
  
  handleKeyboard(e) {
    // Only handle keyboard when modal is open
    if (this.modal?.getAttribute('aria-hidden') === 'true') return;
    
    switch(e.key) {
      case 'Escape':
        e.preventDefault();
        this.closeModal();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.navigateModal('prev');
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.navigateModal('next');
        break;
      case ' ':
        e.preventDefault();
        this.togglePlayPause();
        break;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new UGCVideoCarousel();
  });
} else {
  new UGCVideoCarousel();
}

